import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth, getToken } from "@/lib/auth";
import { track, getGaClientId } from "@/lib/analytics";

const HIGHLIGHT_TIER = "standard"; // visually mark the most popular plan

export default function PricingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [me, setMe] = useState(null);
  const [busyTier, setBusyTier] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then((r) => setTiers(r.data.tiers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || !user.supplier_id) { setMe(null); return; }
    axios.get(`${API}/billing/me`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  const startCheckout = async (tierKey) => {
    const tier = tiers.find((t) => t.key === tierKey);
    track("begin_checkout", {
      currency: "ZAR",
      value: tier?.price_zar || 0,
      items: [{ item_id: tierKey, item_name: tier?.name || tierKey, price: tier?.price_zar || 0, quantity: 1 }],
    });
    if (!user) {
      nav(`/suppliers/register?next=/pricing&tier=${tierKey}`);
      return;
    }
    if (!user.supplier_id) {
      toast.error("Only supplier accounts can subscribe.");
      return;
    }
    setBusyTier(tierKey);
    try {
      const res = await fetch(`${API}/billing/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ tier: tierKey, ga_client_id: getGaClientId() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Checkout failed");
      }
      const html = await res.text();
      // Replace the document with PayFast auto-submit form
      document.open();
      document.write(html);
      document.close();
    } catch (e) {
      toast.error(e.message || "Could not start checkout");
      setBusyTier(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="pricing-page">
      <Helmet>
        <title>Onboarding Special · Supplier plans from R149/month · Spannr</title>
        <meta name="description" content="Spannr supplier subscription plans for SA workshops, scrapyards and dealerships. Onboarding Special: R149–R1499/month. 30-day free trial on Starter. Cancel anytime. Powered by PayFast." />
        <link rel="canonical" href="https://spannr.co.za/pricing" />
      </Helmet>
      <div className="text-center mb-10 sm:mb-14">
        <div className="inline-block mb-2 px-3 py-1 border border-[#FF5100] bg-[#FFF6F0] text-[10px] font-mono-tech tracking-[0.25em] text-[#FF5100] font-bold">
          ONBOARDING SPECIAL · LIMITED TIME
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight text-neutral-900">
          Pricing built for<br />
          <span className="text-[#FF5100]">SA workshops & yards.</span>
        </h1>
        <p className="mt-4 text-neutral-600 max-w-xl mx-auto text-sm sm:text-base">
          Founding-supplier pricing while we grow — lock in your rate today. Start with a 30-day free trial. Cancel anytime. Powered by PayFast — your card stays in South Africa.
        </p>
      </div>

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-4 sm:grid-cols-2">
        {tiers.map((t) => {
          const active = me?.tier === t.key && me?.effective_status !== "cancelled";
          const isHighlight = t.key === HIGHLIGHT_TIER;
          const isEnterprise = t.key === "enterprise";
          return (
            <div
              key={t.key}
              data-testid={`tier-card-${t.key}`}
              className={`flex flex-col p-5 ${
                isHighlight
                  ? "border-2 border-[#FF5100] bg-white relative"
                  : "border border-neutral-200 bg-white"
              }`}
            >
              {isHighlight && (
                <div className="absolute -top-3 left-4 bg-[#FF5100] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 flex items-center gap-1">
                  <Sparkles size={10} /> Most popular
                </div>
              )}

              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">
                {t.name}
              </div>
              <div className="mt-2">
                {t.price_zar === null ? (
                  <div className="font-display text-3xl font-extrabold leading-none">Custom</div>
                ) : (
                  <>
                    <div className="font-display text-3xl font-extrabold leading-none">
                      R{t.price_zar.toLocaleString()}
                      <span className="text-sm font-normal text-neutral-500"> /month</span>
                    </div>
                    {t.key === "starter" && (
                      <div className="text-xs text-emerald-700 font-bold mt-1 uppercase tracking-wider">
                        30 days free
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-3 text-xs text-neutral-500">
                {t.listings_limit === null ? "Unlimited listings" : `Up to ${t.listings_limit} part listings`}
              </div>

              <ul className="mt-4 space-y-2 text-sm flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} className="text-[#FF5100] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5">
                {active ? (
                  <div
                    data-testid={`tier-active-${t.key}`}
                    className="w-full h-11 grid place-items-center text-xs font-bold uppercase tracking-wider border border-neutral-200 bg-neutral-50 text-neutral-500"
                  >
                    Current plan
                  </div>
                ) : isEnterprise ? (
                  <button
                    type="button"
                    data-testid={`tier-contact-${t.key}`}
                    onClick={() => setContactOpen(true)}
                    className={`w-full h-11 inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                      isHighlight
                        ? "bg-[#FF5100] hover:bg-[#E64A00] text-white"
                        : "border border-neutral-300 hover:border-neutral-900 text-neutral-900"
                    }`}
                  >
                    <Mail size={12} /> Contact us
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`tier-cta-${t.key}`}
                    onClick={() => startCheckout(t.key)}
                    disabled={busyTier === t.key}
                    className={`w-full h-11 inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                      isHighlight
                        ? "bg-[#FF5100] hover:bg-[#E64A00] text-white"
                        : "border border-neutral-300 hover:border-neutral-900 text-neutral-900"
                    } disabled:opacity-50`}
                  >
                    {busyTier === t.key
                      ? "Loading..."
                      : t.key === "starter" ? "Start free trial" : `Choose ${t.name}`}
                    <ArrowRight size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center text-xs text-neutral-500 font-mono-tech tracking-wider">
        ALL PLANS · ZAR · MONTHLY · CANCEL ANYTIME · POWERED BY PAYFAST
      </div>

      {contactOpen && <EnterpriseContact onClose={() => setContactOpen(false)} />}
    </div>
  );
}

function EnterpriseContact({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/billing/enterprise-contact`, {
        name, email, company, phone, message,
      });
      track("generate_lead", { tier: "enterprise", company });
      toast.success("Thanks! We'll be in touch within 1 business day.");
      onClose();
    } catch {
      toast.error("Could not send. Please email quotes@spannr.co.za directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
      onClick={onClose}
      data-testid="enterprise-contact-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-neutral-200 max-w-md w-full p-6">
        <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100] mb-1">ENTERPRISE · CUSTOM PRICING</div>
        <h2 className="font-display text-2xl font-extrabold mb-4">Tell us about your operation.</h2>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="ent-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm" />
          <input data-testid="ent-company" required value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Business name" className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm" />
          <input data-testid="ent-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm" />
          <input data-testid="ent-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm" />
          <textarea data-testid="ent-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How many parts do you carry? Anything we should know?" rows={3} className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 py-2 text-sm" />
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-11 border border-neutral-200 hover:border-neutral-300 text-xs uppercase tracking-wider font-bold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="ent-submit" className="flex-1 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white text-xs uppercase tracking-wider font-bold">
              {busy ? "Sending..." : "Send enquiry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
